import React from "react";
import type { SavedOffer } from "../../types/ambassadors";
import { calculate } from "./calc";
import { date, dateTime, handle, pct, usd } from "./format";
import { initials } from "./photo";

// Admin → Ambassadors home: the saved offers, most recently saved first, and
// "New offer". Each row's figures come from that offer's own saved inputs.

interface Props {
  offers: SavedOffer[] | null;
  loadFailed: boolean;
  /** The offer a Duplicate / Delete is running on. */
  busyId: string | null;
  onRetry: () => void;
  onNew: () => void;
  onOpen: (offer: SavedOffer) => void;
  onPresent: (offer: SavedOffer) => void;
  onDuplicate: (offer: SavedOffer) => void;
  onDelete: (offer: SavedOffer) => void;
}

export default function OfferList({ offers, loadFailed, busyId, onRetry, onNew, onOpen, onPresent, onDuplicate, onDelete }: Props) {
  return (
    <div className="adm-card amb-card">
      <div className="amb-card-head">
        <div>
          <h3 className="adm-section-title">Ambassador offers</h3>
          <p className="amb-note">One revenue-share offer per founding ambassador. Each saved offer keeps its own numbers.</p>
        </div>
        <button type="button" className="adm-btn-green" onClick={onNew}>
          + New offer
        </button>
      </div>

      {loadFailed ? (
        <p className="muted-text amb-list-state">
          Couldn’t load saved offers.{" "}
          <button type="button" className="adm-btn-light" onClick={onRetry}>
            Retry
          </button>
        </p>
      ) : offers === null ? (
        <p className="muted-text amb-list-state">Loading saved offers…</p>
      ) : offers.length === 0 ? (
        <p className="muted-text amb-list-state">No saved offers yet. Start one with New offer.</p>
      ) : (
        <div className="adm-table-wrapper amb-scroll-x">
          <table className="adm-table-light amb-list">
            <thead>
              <tr>
                <th>Ambassador</th>
                <th>Partnership</th>
                <th>Revenue share</th>
                <th className="amb-r">Base case total</th>
                <th>Created</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {offers.map((o) => {
                const r = calculate(o.inputs);
                const shares = r.periods.map((p) => pct(p.ratePct)).join(" → ");
                const busy = busyId === o._id;
                return (
                  <tr key={o._id} className="hover-row">
                    <td>
                      <span className="amb-list-dj">
                        <span className="amb-avatar amb-avatar-sm">
                          {o.inputs.photo ? <img src={o.inputs.photo} alt="" /> : initials(o.inputs.djName)}
                        </span>
                        <span className="amb-list-name">
                          <b>{o.inputs.djName}</b>
                          {o.inputs.instagramHandle && <span className="muted-text">{handle(o.inputs.instagramHandle)}</span>}
                        </span>
                      </span>
                    </td>
                    <td className="adm-nowrap">
                      {o.inputs.years} {o.inputs.years === 1 ? "year" : "years"}
                    </td>
                    <td className="amb-list-shares" title={shares}>
                      {shares}
                    </td>
                    <td className="amb-r adm-semibold">{usd(r.totals.base)}</td>
                    <td className="adm-nowrap" title={dateTime(o.createdAt)}>
                      {date(o.createdAt)}
                    </td>
                    <td className="adm-nowrap" title={dateTime(o.updatedAt)}>
                      {date(o.updatedAt)}
                    </td>
                    <td>
                      <div className="action-btn-group amb-list-actions">
                        <button type="button" className="adm-btn-light" onClick={() => onOpen(o)}>
                          Open
                        </button>
                        <button
                          type="button"
                          className="adm-btn-light"
                          disabled={r.issues.length > 0}
                          title={r.issues.length ? `Still needed: ${r.issues.join(", ")}` : undefined}
                          onClick={() => onPresent(o)}
                        >
                          Present
                        </button>
                        <button type="button" className="adm-btn-ghost" disabled={busy} onClick={() => onDuplicate(o)}>
                          Duplicate
                        </button>
                        <button type="button" className="adm-btn-ghost amb-danger" disabled={busy} onClick={() => onDelete(o)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
